import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { GlobalService } from './global.service';
import { Observable, throwError } from 'rxjs';
import { environment } from '@shared-envs';
import { catchError, share } from 'rxjs/operators';
import { StorageService } from '@services/storage.service';
import {
  FilterRequest,
  HttpMethods,
  PagedResponse,
  PaginationRequest,
} from '@interfaces/common.interface';
import {
  ApiAuthChallengeResponse,
  ApiAuthInviteRequest,
  ApiAuthLoginRequest,
  ApiAuthLoginResponse,
  ApiAuthPassCreateRequest,
  ApiAuthPassResetRequest,
  ApiAuthRefreshTokenResponse,
} from '@interfaces/api.auth.interface';
import {
  Proposal,
  ProposalAllRequest,
  ProposalRequest,
} from '@interfaces/api.proposal.interface';
import { Category } from '@interfaces/api.category.interface';
import {
  ApiMembershipLevelResponse,
  ApiMembershipLevelsResponse,
  ApiMembershipPurchaseRequest,
  ApiMembershipPurchaseResponse,
  ApiMembershipTokensResponse,
} from '@interfaces/api.membership.interface';
import {
  ApiAdminInviteRequest,
  ApiAdminInviteResponse,
  ApiAdminUserEditRequest,
  ApiAdminUsersRequest,
  ApiAdminUsersResponse,
} from '@interfaces/api.admin.interface';
import {
  ApiAccountProfileEdit,
  ApiAccountProfileResponse,
  newSettings,
  Settings,
} from '@interfaces/api.account.interface';
import { ApiCategoryStatisticResponse } from '@interfaces/api.statistic.interface';
import {
  ApiForumCommentsAddResponse,
  CertificationTypeId,
} from '@interfaces/api.forum.interface';
import { ApiStatisticResponse } from '@interfaces/statistic.api.interface';
import {
  IDiscussion,
  IDiscussionAllResponse,
  IDiscussionsAllRequest,
} from '@interfaces/discussion.interface';
import {
  IProposalsRequest,
  StatisticsMine,
  Track,
} from '@interfaces/proposal.interfase';
import {
  ApiLockCheckResponse,
  ApiNftAllResponse,
  Nft,
} from '@interfaces/api.nft.interface';
import {
  IDiscussionSearchAllResponse,
  IProposalSearchAllResponse,
} from '@interfaces/api.unified-search.interface';
import {
  ActiveSurvey,
  Survey,
  SurveyAllRequest,
  SurveyAllResponse,
} from '@interfaces/api.survey.interface';
import { INotificationResponse } from '@interfaces/notifications.interfase';
import { ValueResponse } from '@interfaces/api.value.interface';
import {
  Certification,
  CertificationSingle,
} from '@interfaces/api.certification.interface';
import { Post, PostGet } from '@interfaces/api.post.interface';
import { Country } from '@interfaces/api.country.interface';
import { GeneralSettingApiResponse } from '@interfaces/api.general.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  statistic = {
    get: (): Observable<ApiStatisticResponse> => {
      return this.fetch(`Statistics`, 'get');
    },
  };

  general = {
    settings: (): Observable<GeneralSettingApiResponse[]> => {
      return this.fetch(`General/general/settings`, `get`);
    },
  };

  country = {
    countries: (): Observable<Country[]> => {
      return this.fetch(`General/general/countries`, `get`);
    },
  };

  userRelations = {
    follow: (userId: number): Observable<any> => {
      return this.fetch(`UserRelations/follow/${userId}`, `get`);
    },
    unfollow: (userId: number): Observable<any> => {
      return this.fetch(`UserRelations/unfollow/${userId}`, `get`);
    },
    block: (userId: number): Observable<any> => {
      return this.fetch(`UserRelations/block/${userId}`, `get`);
    },
    unblock: (userId: number): Observable<any> => {
      return this.fetch(`UserRelations/unblock/${userId}`, `get`);
    },
    userRelations: (targetUserId: number): Observable<any> => {
      return this.fetch(`UserRelations/userRelations/${targetUserId}`, `get`);
    },
  };

  values = {
    post: (filter: FilterRequest): Observable<PagedResponse<ValueResponse>> => {
      return this.fetch(`Values`, `post`, filter);
    },
    subscribe: (valueId: number, weight: number): Observable<any> => {
      return this.fetch(`Values/subscribe/${valueId}?weight=${weight}`, `post`);
    },
    unsubscribe: (valueId: number): Observable<any> => {
      return this.fetch(`Values/subscribe/${valueId}`, `delete`);
    },
    weight: (valueId: number, weight: number): Observable<any> => {
      return this.fetch(
        `Values/subscribe/${valueId}/weight?weight=${weight}`,
        `post`,
      );
    },
    mine: (): Observable<ValueResponse[]> => {
      return this.fetch(`Values/mine`, `get`);
    },
  };

  certification = {
    search: (
      filter: FilterRequest,
    ): Observable<PagedResponse<Certification>> => {
      return this.fetch(`Certification/search`, `post`, filter);
    },
    subscribe: (certificationId: number, weight: number): Observable<any> => {
      return this.fetch(
        `Certification/subscribe/${certificationId}?weight=${weight}`,
        `post`,
      );
    },
    unsubscribe: (certificationId: number): Observable<any> => {
      return this.fetch(`Certification/subscribe/${certificationId}`, `delete`);
    },
    weight: (certificationId: number, weight: number): Observable<any> => {
      return this.fetch(
        `Certification/subscribe/${certificationId}/weight?weight=${weight}`,
        `post`,
      );
    },
    mine: (filter: FilterRequest): Observable<PagedResponse<Certification>> => {
      return this.fetch(`Certification/mine`, `post`, filter);
    },
    get: (id: number): Observable<CertificationSingle> => {
      return this.fetch(`Certification?id=${id}`, `get`);
    },
  };

  auth = {
    login: (data: ApiAuthLoginRequest): Observable<ApiAuthLoginResponse> => {
      return this.fetch(`auth/login`, 'post', data);
    },
    invite: (data: ApiAuthInviteRequest): Observable<any> => {
      return this.fetch(`auth/invite`, 'post', data);
    },
    password: {
      reset: (data: ApiAuthPassResetRequest): Observable<any> => {
        return this.fetch(`auth/password/reset`, 'post', data);
      },
      create: (data: ApiAuthPassCreateRequest): Observable<any> => {
        return this.fetch(`auth/password/create`, 'post', data);
      },
    },
    challenge: (walletId: string): Observable<ApiAuthChallengeResponse> => {
      return this.fetch(`Auth/challenge/${walletId}`, 'get');
    },
    refreshToken: {
      get: (refreshToken: string): Observable<ApiAuthRefreshTokenResponse> => {
        return this.fetch(`Auth/refreshToken`, 'post', { refreshToken });
      },
    },
  };

  notifications = {
    list: (): Observable<INotificationResponse> => {
      return this.fetch(`Notifications`, 'get');
    },
    read: (id: number): Observable<any> => {
      return this.fetch(`Notifications/read/${id}`, 'post');
    },
    delete: (id: number): Observable<any> => {
      return this.fetch(`Notifications?notificationId=${id}`, 'delete');
    },
  };

  account = {
    profile: {
      get: (): Observable<ApiAccountProfileResponse> => {
        return this.fetch(`Account/profile`, 'get');
      },
      edit: (data: ApiAccountProfileEdit): Observable<any> => {
        return this.fetch(`Account/update/profile`, 'put', data);
      },
      view: (userId: number): Observable<ApiAccountProfileResponse> => {
        return this.fetch(`Account/profile/view/${userId}`, 'get');
      },
    },
    completeWalkthrough: (): Observable<ApiAccountProfileResponse> => {
      return this.fetch(`Account/completeWalkthrough`, 'get');
    },
    invitation: {
      send: (data: any): Observable<any> => {
        return this.fetch(`Invitation/invite/v2`, 'post', data);
      },
      request: (data: any): Observable<any> => {
        return this.fetch(`Invitation/invitation/request`, 'post', data);
      },
      sendRequest: (id: number, data: any): Observable<any> => {
        return this.fetch(
          `Invitation/invitation/request/send/${id}`,
          'post',
          data,
        );
      },
      listInvitationRequests: (): Observable<any> => {
        return this.fetch(`Invitation/invitation/requests`, 'get');
      },
      stats: (): Observable<any> => {
        return this.fetch(`Invitation/invitation/stats`, 'get');
      },
    },
    settings: {
      set: (data: Settings): Observable<Settings> => {
        return this.fetch(`UserSettings`, 'post', data);
      },
      get: (): Observable<Settings> => {
        return this.fetch(`UserSettings`, 'get');
      },
    },
    newSettings: {
      set: (data: newSettings): Observable<newSettings> => {
        return this.fetch(`UserSettings`, 'post', data);
      },
      get: (): Observable<newSettings> => {
        return this.fetch(`UserSettings`, 'get');
      },
    },
  };

  post = {
    get: (postId: number): Observable<PostGet> => {
      return this.fetch(`Post?id=${postId}`, 'get');
    },
    boost: (postId: number, budgetCredits: number): Observable<any> => {
      return this.fetch(`Post/${postId}/boost`, 'post', { budgetCredits });
    },
    cancel: (postId: number): Observable<any> => {
      return this.fetch(`Post/${postId}/cancel`, 'post');
    },
    delete: (postId: number): Observable<any> => {
      return this.fetch(`Post/${postId}/delete`, 'post');
    },
    pause: (postId: number): Observable<any> => {
      return this.fetch(`Post/${postId}/pause`, 'post');
    },
    resume: (postId: number): Observable<any> => {
      return this.fetch(`Post/${postId}/resume`, 'post');
    },
    post: (data: Post): Observable<Post> => {
      return this.fetch(`Post`, 'post', data);
    },
    mine: (filter: FilterRequest): Observable<PagedResponse<Post>> => {
      return this.fetch(`Post/mine`, 'post', filter);
    },
    timeline: (filter: FilterRequest): Observable<PagedResponse<Post>> => {
      return this.fetch(`Post/timeline`, 'post', filter);
    },
    rate: (postId: number, value: number): Observable<any> => {
      return this.fetch(`Post/${postId}/rate/${value}`, 'post');
    },
    upvote: (postId: number): Observable<any> => {
      return this.fetch(`Post/${postId}/upvote`, 'post');
    },
    downvote: (postId: number): Observable<any> => {
      return this.fetch(`Post/${postId}/downvote`, 'post');
    },
    flag: {
      flag: (postId: number, reason: string): Observable<any> => {
        return this.fetch(`Post/flag/${postId}`, 'post', { reason });
      },
      reasons: (): Observable<string[]> => {
        return this.fetch(`Post/flag/reasons`, 'get');
      },
      upvote: (postId: number): Observable<any> => {
        return this.fetch(`Post/${postId}/moderation/upvote`, 'post');
      },
      downvote: (postId: number): Observable<any> => {
        return this.fetch(`Post/${postId}/moderation/downvote`, 'post');
      },
    },
    moderate: (postId: number): Observable<any> => {
      return this.fetch(`Post/moderate/${postId}`, 'post');
    },
  };

  imageUpload = {
    post: (file: File): Observable<any> => {
      return this.fetch(`ImageUpload`, 'upload', null, file);
    },
  };

  proposal = {
    list: {
      post: (data: ProposalAllRequest): Observable<IDiscussionAllResponse> => {
        return this.fetch(`CertificationProposal/all`, 'post', data);
      },
    },
    getById: {
      get: (id: number): Observable<IDiscussion[]> => {
        return this.fetch(`CertificationProposal/${id}`, 'get');
      },
    },
    create: {
      post: (data: ProposalRequest): Observable<Proposal> => {
        return this.fetch(`CertificationProposal`, 'post', data);
      },
    },
    update: {
      put: (data: ProposalRequest): Observable<Proposal> => {
        return this.fetch(`CertificationProposal`, 'put', data);
      },
    },
    like: {
      post: (id: number): Observable<any> => {
        return this.fetch(`CertificationProposal/${id}/like`, 'post');
      },
      delete: (id: number): Observable<any> => {
        return this.fetch(`CertificationProposal/${id}/like`, 'delete', null);
      },
    },
    delete: {
      delete: (id: number): Observable<any> => {
        return this.fetch(`CertificationProposal/${id}`, 'delete');
      },
    },
  };

  category = {
    list: {
      get: (): Observable<Category[]> => {
        return this.fetch(`CertificationCategory/all`, 'get');
      },
    },
    getById: {
      get: (id: number): Observable<Category> => {
        return this.fetch(`CertificationCategory/${id}`, 'get');
      },
    },
    create: {
      post: (data: Category): Observable<any> => {
        return this.fetch(`CertificationCategory`, 'post', data);
      },
    },
    update: {
      put: (data: any): Observable<any> => {
        return this.fetch(`CertificationCategory`, 'put', data);
      },
    },
    delete: {
      delete: (id: number): Observable<any> => {
        return this.fetch(`CertificationCategory/${id}`, 'delete');
      },
    },
  };

  membership = {
    levels: (): Observable<ApiMembershipLevelsResponse> => {
      return this.fetch(`Membership/levels`, 'get');
    },
    purchase: (
      data: ApiMembershipPurchaseRequest,
    ): Observable<ApiMembershipPurchaseResponse> => {
      return this.fetch(`Membership/purchase`, 'post', data);
    },
    level: (): Observable<ApiMembershipLevelResponse> => {
      return this.fetch(`Membership/user/level`, 'get');
    },
    tokens: (): Observable<ApiMembershipTokensResponse> => {
      return this.fetch(`Membership/user/tokens`, 'get');
    },
  };

  nft = {
    all: (
      membershipId: number,
      data: PaginationRequest,
    ): Observable<ApiNftAllResponse> => {
      return this.fetch(`nft/${membershipId}/all`, 'post', data);
    },
    lock: (nftItemId: number): Observable<any> => {
      return this.fetch(`nft/lock/${nftItemId}`, 'get');
    },
    unlock: (nftItemId: number): Observable<any> => {
      return this.fetch(`nft/unlock/${nftItemId}`, 'get');
    },
    get: (nftItemId: number): Observable<Nft> => {
      return this.fetch(`nft/${nftItemId}`, 'get');
    },
    my: (): Observable<Nft[]> => {
      return this.fetch(`nft/myNfts`, 'get');
    },
    lockCheck: (nftItemId: number): Observable<ApiLockCheckResponse> => {
      return this.fetch(`nft/lockCheck/${nftItemId}`, 'get');
    },
    badge: (nftItemId: number): Observable<any> => {
      return this.fetch(
        `nft/myNfts/downloadBadge?nftItemId=${nftItemId}`,
        'downloadImage',
      );
    },
  };

  statistics = {
    byCategory: {
      get: (categoryId: number): Observable<ApiCategoryStatisticResponse> => {
        return this.fetch(`statistics/byCategory/${categoryId}`, 'get');
      },
    },
  };
  admin = {
    invite: (
      data: ApiAdminInviteRequest,
    ): Observable<ApiAdminInviteResponse> => {
      return this.fetch(`UserManagement/invite`, 'post', data);
    },
    users: (data: ApiAdminUsersRequest): Observable<ApiAdminUsersResponse> => {
      return this.fetch(`UserManagement/users`, 'post', data);
    },
    user: {
      get: (userId: number): Observable<ApiAdminUsersResponse> => {
        return this.fetch(`UserManagement/user?userId=${userId}`, 'get');
      },
      put: (
        userId: number,
        data: ApiAdminUserEditRequest,
      ): Observable<ApiAdminUsersResponse> => {
        return this.fetch(`UserManagement/user?userId=${userId}`, 'put', data);
      },
      delete: (userId: number): Observable<ApiAdminUsersResponse> => {
        return this.fetch(`UserManagement/user?userId=${userId}`, 'delete');
      },
    },
  };

  request = {
    list: {
      get: (data: any): Observable<any> => {
        return this.fetch(`CertificationRequest/all`, 'post', data);
      },
    },
    show: {
      get: (requestId: number): Observable<any> => {
        return this.fetch(`CertificationRequest/${requestId}`, 'get');
      },
    },
    delete: (requestId: number): Observable<any> => {
      return this.fetch(`CertificationRequest/${requestId}`, 'delete');
    },
    create: {
      post: (data: any): Observable<any> => {
        return this.fetch(`CertificationRequest`, 'post', data);
      },
    },
    update: {
      put: (data: any): Observable<any> => {
        return this.fetch(`CertificationRequest`, 'put', data);
      },
    },
    statistics: {
      get: (): Observable<StatisticsMine> => {
        return this.fetch(`CertificationRequest/stats/mine`, 'get');
      },
    },
    types: (): Observable<any> => {
      return this.fetch(`CertificationRequest/certificationTypes`, 'get');
    },
    track: (data: Track): Observable<any> => {
      return this.fetch(`Action/social/share`, 'post', data);
    },
    all: (data: any): Observable<any> => {
      return this.fetch(`CertificationRequest/all`, 'post', data);
    },
    back: (data: any): Observable<any> => {
      return this.fetch(`CertificationRequest/support/add`, 'post', data);
    },
    removeBack: (data: any): Observable<any> => {
      return this.fetch(`CertificationRequest/support/remove`, 'post', data);
    },
    updateBack: (data: any): Observable<any> => {
      return this.fetch(`CertificationRequest/support/update`, 'post', data);
    },
    like: (requestId: number): Observable<any> => {
      return this.fetch(`CertificationRequest/${requestId}/like`, 'post', null);
    },
    dislike: (requestId: number): Observable<any> => {
      return this.fetch(
        `CertificationRequest/${requestId}/like`,
        'delete',
        null,
      );
    },
    favorite: {
      add: (requestId: string): Observable<any> => {
        return this.fetch(
          `CertificationRequest/${requestId}/favorite`,
          'post',
          null,
        );
      },
      delete: (requestId: string): Observable<any> => {
        return this.fetch(
          `CertificationRequest/${requestId}/favorite`,
          'delete',
          null,
        );
      },
    },
  };

  forum = {
    discussion: {
      add: (
        certificationTypeId: CertificationTypeId,
        certificationId: number,
        body: string,
      ): Observable<ApiForumCommentsAddResponse> => {
        return this.fetch(
          `DiscussionForum/${certificationTypeId}/${certificationId}`,
          'post',
          { body },
        );
      },
    },
    comment: {
      all: (
        certificationTypeId: CertificationTypeId,
        certificationId: number,
      ): Observable<any> => {
        return this.fetch(
          `DiscussionForum/${certificationTypeId}/${certificationId}`,
          'get',
        );
      },
      add: (
        certificationTypeId: CertificationTypeId,
        certificationId: number,
        body: string,
      ): Observable<ApiForumCommentsAddResponse> => {
        return this.fetch(
          `DiscussionForum/${certificationTypeId}/${certificationId}/comment`,
          'post',
          { body },
        );
      },
      edit: (postCommentId: number, body: string): Observable<any> => {
        return this.fetch(
          `DiscussionForum/discussion/comment/${postCommentId}`,
          'put',
          { body },
        );
      },
      delete: (postCommentId: number): Observable<any> => {
        return this.fetch(
          `DiscussionForum/discussion/comment/${postCommentId}`,
          'delete',
        );
      },
    },
    like: {
      add: (postCommentId: number): Observable<any> => {
        return this.fetch(
          `DiscussionForum/discussion/comment/${postCommentId}/like`,
          'get',
        );
      },
      delete: (postCommentId: number): Observable<any> => {
        return this.fetch(
          `DiscussionForum/discussion/comment/${postCommentId}/like`,
          'delete',
        );
      },
    },
  };

  search = {
    discussions: {
      all: (
        data: IDiscussionsAllRequest,
      ): Observable<IDiscussionSearchAllResponse> => {
        return this.fetch(`Search/discussions/all`, 'post', data);
      },
    },
    proposals: {
      all: (
        data: IProposalsRequest,
      ): Observable<IProposalSearchAllResponse> => {
        return this.fetch(`Search/proposals/all`, 'post', data);
      },
    },
  };

  survey = {
    list: {
      get: (data: SurveyAllRequest): Observable<SurveyAllResponse> => {
        return this.fetch(`Survey/all`, 'post', data);
      },
    },
    create: {
      post: (data: Survey): Observable<any> => {
        return this.fetch(`Survey`, 'post', data);
      },
    },
    update: {
      put: (data: Survey): Observable<any> => {
        return this.fetch(`Survey`, 'put', data);
      },
    },
    delete: {
      delete: (id: number): Observable<any> => {
        return this.fetch(`Survey/${id}`, 'delete');
      },
    },
    active: {
      get: (): Observable<ActiveSurvey> => {
        return this.fetch(`Survey/activeSurvey`, 'post');
      },
    },
  };

  private readonly apiURL: string;

  constructor(
    private storage: StorageService,
    private global: GlobalService,
    private http: HttpClient,
  ) {
    this.apiURL = environment.apiBaseUrl;
  }

  private static handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(`Server Error ${error.status}:`, error.error);
    }
    // return an ErrorObservable with a user-facing error message
    return throwError(error);
  }

  fetch(
    endpoint: string,
    method: HttpMethods,
    data?: any,
    file?: any | null,
    mock?: boolean,
  ): Observable<any> {
    const jData = data ? JSON.stringify(data) : '';
    const url = this.apiURL + '/' + endpoint;
    if (mock) {
      return this.http.get(
        `./assets/mocks/${endpoint.replace(
          '/',
          '.',
        )}.${method.toUpperCase()}.json`,
      );
    } else {
      switch (method) {
        case 'get':
          return this.http
            .get(url)
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'post':
          return this.http
            .post(url, jData)
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'patch':
          return this.http
            .patch(url, jData)
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'upload':
          const formData: FormData = new FormData();
          if (data) {
            Object.keys(data).forEach((item) => {
              formData.append(item[0], item[1]);
            });
          }
          if (file) {
            formData.append('file', file);
          }
          return this.http
            .post(url, formData)
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'delete':
          return this.http
            .delete(url)
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'put':
          return this.http
            .put(url, jData)
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'download':
          return this.http
            .get(url, { responseType: 'arraybuffer' })
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'downloadImage':
          return this.http
            .get(url, { responseType: 'blob', observe: 'response' })
            .pipe(share())
            .pipe(catchError((error) => ApiService.handleError(error)));
        case 'multipart':
          break;
      }
      return this.http.get(url);
    }
  }
}
